/**
 * AttachFilesDialog Component
 * Dialog for attaching files to multiple agents
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Typography,
  CircularProgress,
  Box,
  Alert,
  Chip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import adminApi from '@/services/admin.api';

interface Agent {
  _id: string;
  name: string;
  topics?: string[];
}

interface AttachFilesDialogProps {
  open: boolean;
  fileIds: string[];
  fileName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const AttachFilesDialog: React.FC<AttachFilesDialogProps> = ({
  open,
  fileIds,
  fileName,
  onClose,
  onSuccess,
}) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [attachedAgents, setAttachedAgents] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAttached, setLoadingAttached] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [error, setError] = useState('');

  // Memoize the first file ID to avoid unnecessary re-renders
  const firstFileId = useMemo(() => fileIds[0] || '', [fileIds.join(',')]);

  const loadAgents = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminApi.getAgents();
      setAgents(response.data || []);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        'Failed to load agents';
      setError(errorMessage);
      console.error('Failed to load agents:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAttachedAgents = useCallback(async () => {
    if (!firstFileId) {
      setAttachedAgents([]);
      return;
    }

    try {
      setLoadingAttached(true);
      // Get agents for the first file (assuming single file attachment)
      const agents = await adminApi.getFileAgents(firstFileId);
      setAttachedAgents(agents.map((a) => a._id));
    } catch (err: any) {
      console.error('Failed to load attached agents:', err);
      // Don't show error for this, just set empty array
      setAttachedAgents([]);
    } finally {
      setLoadingAttached(false);
    }
  }, [firstFileId]);

  useEffect(() => {
    if (open && fileIds.length > 0) {
      loadAgents();
      loadAttachedAgents();
      setSelectedAgents([]);
      setError('');
    } else {
      // Reset state when dialog closes
      setSelectedAgents([]);
      setAttachedAgents([]);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, firstFileId]);

  const handleToggleAgent = useCallback((agentId: string) => {
    setSelectedAgents((prev) =>
      prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId]
    );
  }, []);

  const handleDetachAgent = useCallback(
    async (agentId: string) => {
      if (!firstFileId) return;

      try {
        setError('');
        await adminApi.detachFileFromAgent(agentId, firstFileId, false);
        setAttachedAgents((prev) => prev.filter((id) => id !== agentId));
        // Reload agents list to update the UI
        await loadAttachedAgents();
        onSuccess(); // Refresh parent
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.error?.message ||
          err.response?.data?.message ||
          err.message ||
          'Failed to detach file';
        setError(errorMessage);
        console.error('Failed to detach file:', err);
      }
    },
    [firstFileId, loadAttachedAgents, onSuccess]
  );

  const handleAttach = useCallback(async () => {
    if (selectedAgents.length === 0) {
      setError('Please select at least one agent');
      return;
    }

    if (fileIds.length === 0) {
      setError('No files selected');
      return;
    }

    try {
      setAttaching(true);
      setError('');
      await adminApi.attachFilesToAgents(fileIds, selectedAgents);
      // Update attached agents list
      setAttachedAgents((prev) => [...new Set([...prev, ...selectedAgents])]);
      setSelectedAgents([]);
      // Reload to ensure consistency
      await loadAttachedAgents();
      onSuccess();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        'Failed to attach files';
      setError(errorMessage);
      console.error('Failed to attach files:', err);
    } finally {
      setAttaching(false);
    }
  }, [fileIds, selectedAgents, loadAttachedAgents, onSuccess]);

  const handleClose = useCallback(() => {
    if (!attaching) {
      setSelectedAgents([]);
      setAttachedAgents([]);
      setError('');
      onClose();
    }
  }, [attaching, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Attach File to Agents</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select agents to attach &quot;{fileName}&quot; to:
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Show already attached agents */}
        {loadingAttached ? (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading attached agents...
            </Typography>
          </Box>
        ) : attachedAgents.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Already attached to:
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {agents
                .filter((agent) => attachedAgents.includes(agent._id))
                .map((agent) => (
                  <Chip
                    key={agent._id}
                    label={agent.name}
                    size="small"
                    onDelete={() => handleDetachAgent(agent._id)}
                    deleteIcon={<CloseIcon />}
                    color="primary"
                    variant="outlined"
                  />
                ))}
            </Box>
          </Box>
        ) : null}

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : agents.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" p={3}>
            No agents available
          </Typography>
        ) : (
          <List>
            {agents
              .filter((agent) => !attachedAgents.includes(agent._id)) // Only show unattached agents
              .map((agent) => (
                <ListItem
                  key={agent._id}
                  button
                  onClick={() => handleToggleAgent(agent._id)}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Checkbox
                    checked={selectedAgents.includes(agent._id)}
                    onChange={() => handleToggleAgent(agent._id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <ListItemText
                    primary={agent.name}
                    secondary={agent.topics?.length ? agent.topics.join(', ') : 'No topics'}
                  />
                </ListItem>
              ))}
          </List>
        )}

        {selectedAgents.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            {selectedAgents.length} agent(s) selected
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={attaching}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAttach}
          disabled={attaching || selectedAgents.length === 0 || loading}
        >
          {attaching ? 'Attaching...' : 'Attach'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachFilesDialog;
